/**
 * ChatMention hook — unified @ autocomplete for the App Builder chat.
 *
 *   @filename.tsx     — file in the current app (emerald badge)
 *   @App Name         — another accessible app (info badge)
 *   @Agent Name       — workspace agent (amber badge)
 *   @tool_name        — workspace custom tool (info badge)
 *   @Workflow Name    — workspace workflow trigger (rose badge)
 *
 * All mention types use @ as prefix, differentiated by icon/color in the dropdown.
 *
 * Dropdown lives on document.body to survive LiveView DOM patches.
 * Uses event delegation on the dropdown (single listener, survives innerHTML).
 */
export default {
  mounted() {
    this.textarea = this.el
    this.candidates = []
    this.allFiles = []
    this.allApps = []
    this.allAgents = []
    this.allTools = []
    this.allWorkflows = []
    this.allDatapacks = []
    this.mentionStart = -1
    this.selectedIdx = 0
    this.isOpen = false
    this._skipNextBlur = false

    this._loadData()
    this.pushEvent("request_mention_candidates", {})

    this.handleEvent("update_mention_candidates", ({ files, apps, agents, tools, workflows, datapacks, context_blocks }) => {
      this.allFiles = files || []
      this.allApps = apps || []
      this.allAgents = agents || []
      this.allTools = tools || []
      this.allWorkflows = workflows || []
      this.allDatapacks = datapacks || []
      this.allContextBlocks = context_blocks || []
    })

    this._createDropdown()
    // Don't call _resize() on mount — the inline height: 34px is the correct
    // 1-line height. _resize() is triggered on input events to grow as needed.

    // Overlay for styled mentions in the input. Each textarea can name its
    // own overlay via `data-mention-overlay="<element-id>"`; falls back to
    // the legacy global ID for backward compat with the App Builder chat.
    //
    // Progressive enhancement: only flip the textarea text to transparent
    // (so the styled overlay takes over) when we actually found the overlay.
    // If it's missing for any reason in prod (stale asset, hook failed before
    // this line, etc.), the textarea keeps its native color and the user
    // still sees what they type — at worst losing the @mention badge styling.
    const overlayId = this.textarea.dataset.mentionOverlay || "chat-mention-overlay"
    this.overlay = document.getElementById(overlayId)
    if (this.overlay) {
      this.textarea.style.color = "transparent"
      this.textarea.style.webkitTextFillColor = "transparent"
    }

    // A throw mid-handler (e.g. a candidate with a null name inside
    // _detectMention/_filter) used to leave the dropdown stuck open with
    // `dataset.mentionOpen` set, swallowing every Enter. Fail closed instead.
    this._inputHandler = () => {
      try {
        this._resize()
        this._detectMention()
        this._syncOverlay()
      } catch (err) {
        console.error("[ChatMention] input handler error:", err)
        try { this._close() } catch (_e) { /* keep the composer usable */ }
      }
    }
    this._keydownHandler = (e) => this._onKeydown(e)
    this._blurHandler = () => {
      // If a dropdown click set _skipNextBlur, don't close
      if (this._skipNextBlur) {
        this._skipNextBlur = false
        return
      }
      setTimeout(() => {
        // Only close if textarea doesn't have focus (user clicked elsewhere)
        if (document.activeElement !== this.textarea) {
          this._close()
        }
      }, 150)
    }

    this._scrollHandler = () => {
      if (this.overlay) this.overlay.scrollTop = this.textarea.scrollTop
    }

    this.textarea.addEventListener("input", this._inputHandler)
    this.textarea.addEventListener("keydown", this._keydownHandler)
    this.textarea.addEventListener("blur", this._blurHandler)
    this.textarea.addEventListener("scroll", this._scrollHandler)

    // Initial overlay sync
    this._syncOverlay()
  },

  updated() {
    // Throwing inside updated() aborts the LiveView DOM patch — keep this
    // resilient so a single bad mention candidate cannot freeze the chat.
    try {
      // Only resize if there's actual content — empty textarea keeps its 1-line height
      if (this.textarea.value) this._resize()
      this._loadData()
      this._syncOverlay()
      if (this.isOpen) this._detectMention()
    } catch (err) {
      console.error("[ChatMention] updated() error:", err)
    }
  },

  destroyed() {
    if (this.dropdown?.parentNode) this.dropdown.parentNode.removeChild(this.dropdown)
    if (this.textarea) {
      // A leftover mentionOpen would make the inline Enter guard swallow
      // submits after a reconnect remounts the hook on the same element.
      delete this.textarea.dataset.mentionOpen
      this.textarea.style.color = ""
      this.textarea.style.webkitTextFillColor = ""
      this.textarea.removeEventListener("input", this._inputHandler)
      this.textarea.removeEventListener("keydown", this._keydownHandler)
      this.textarea.removeEventListener("blur", this._blurHandler)
      this.textarea.removeEventListener("scroll", this._scrollHandler)
    }
  },

  _loadData() {
    try { this.allFiles = JSON.parse(this.textarea.dataset.mentionFiles || "[]") } catch (_) {}
    try { this.allApps = JSON.parse(this.textarea.dataset.mentionApps || "[]") } catch (_) {}
    try { this.allAgents = JSON.parse(this.textarea.dataset.mentionAgents || "[]") } catch (_) {}
    try { this.allTools = JSON.parse(this.textarea.dataset.mentionTools || "[]") } catch (_) {}
    try { this.allWorkflows = JSON.parse(this.textarea.dataset.mentionWorkflows || "[]") } catch (_) {}
    try { this.allDatapacks = JSON.parse(this.textarea.dataset.mentionDatapacks || "[]") } catch (_) {}
    try { this.allContextBlocks = JSON.parse(this.textarea.dataset.mentionContextBlocks || "[]") } catch (_) {}
  },

  _resize() {
    const el = this.textarea
    const maxLines = parseInt(el.dataset.maxLines || "3")
    const lh = parseInt(getComputedStyle(el).lineHeight) || 20
    const pt = parseInt(getComputedStyle(el).paddingTop) || 0
    const pb = parseInt(getComputedStyle(el).paddingBottom) || 0
    const maxH = maxLines * lh + pt + pb
    const minH = lh + pt + pb
    el.style.height = "auto"
    el.style.height = Math.max(Math.min(el.scrollHeight, maxH), minH) + "px"
    el.style.overflowY = el.scrollHeight > maxH ? "auto" : "hidden"
    // Sync overlay height and scroll behavior
    if (this.overlay) {
      this.overlay.style.height = el.style.height
      this.overlay.style.overflowY = el.style.overflowY
    }
  },

  // Build combined known mentions list with their colors for overlay
  _buildKnownMentions() {
    const mentions = []
    // A null/empty name used to crash sort() inside updated(), aborting the
    // LiveView DOM patch. Filter aggressively here.
    const push = (name, color, type) => {
      if (typeof name === "string" && name.length > 0) mentions.push({ name, color, type })
    }
    // Files — match by full path (e.g. "src/App.tsx")
    this.allFiles.forEach(f => push(f.path, "var(--accent-primary)", "file"))
    // Apps — match by name
    this.allApps.forEach(a => push(a.name, "var(--info)", "app"))
    // Agents — match by name
    this.allAgents.forEach(a => push(a.name, "var(--warn)", "agent"))
    // Tools — match by name (snake_case)
    this.allTools.forEach(t => push(t.name, "var(--info)", "tool"))
    // Workflows — match by name
    this.allWorkflows.forEach(w => push(w.name, "var(--badge-workflow)", "workflow"))
    // Datapacks — table-level (pack/table) and pack-level (whole pack).
    // Sorting by descending length ensures `data:fcl/ledger_entries` is matched
    // before `data:fcl` so the table-level highlight wins inside `@data:fcl/x`.
    this.allDatapacks.forEach(d => {
      if (d && d.pack_key && d.table_name) {
        push(`data:${d.pack_key}/${d.table_name}`, "var(--s-300)", "datapack")
      }
    })
    const seenPacks = new Set()
    this.allDatapacks.forEach(d => {
      if (d && d.pack_key && !seenPacks.has(d.pack_key)) {
        seenPacks.add(d.pack_key)
        push(`data:${d.pack_key}`, "var(--s-300)", "datapack_pack")
      }
    })
    return mentions.sort((a, b) => b.name.length - a.name.length)
  },

  // ── Overlay: render styled mentions behind the textarea ───────────

  _syncOverlay() {
    if (!this.overlay) return
    const val = this.textarea.value
    if (!val) {
      this.overlay.innerHTML = ""
      return
    }

    const esc = val.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

    // Highlight all known @mentions by name, longest first
    const known = this._buildKnownMentions()
    let highlighted = esc
    if (known.length > 0) {
      const escaped = known.map(m => m.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      const re = new RegExp("@(" + escaped.join("|") + ")(?=\\s|$)", "g")
      highlighted = esc.replace(re, (_m, name) => {
        const m = known.find(k => k.name === name)
        const color = m ? m.color : "var(--accent-primary)"
        return `<span style="color:${color};font-weight:600">@${name}</span>`
      })
    }

    this.overlay.innerHTML = highlighted
    this.overlay.scrollTop = this.textarea.scrollTop
  },

  // ── Dropdown (body-mounted, event delegation) ─────────────────────

  _createDropdown() {
    const dd = document.createElement("div")
    dd.className = "chat-mention-dropdown"
    Object.assign(dd.style, {
      display: "none", position: "fixed", zIndex: "10001",
      maxHeight: "200px", overflowY: "auto", borderRadius: "8px",
      border: "1px solid var(--border-default)", background: "var(--bg-card)",
      boxShadow: "0 4px 16px rgba(0,0,0,0.12)", padding: "4px 0",
    })

    // EVENT DELEGATION — single listener, survives innerHTML replacements
    dd.addEventListener("mousedown", (e) => {
      // Prevent textarea blur
      e.preventDefault()
      // Find the clicked item
      const item = e.target.closest(".cm-item")
      if (!item) return
      const idx = parseInt(item.dataset.idx)
      if (idx >= 0 && idx < this.candidates.length) {
        this._skipNextBlur = true
        this._select(this.candidates[idx])
      }
    })

    dd.addEventListener("mouseover", (e) => {
      const item = e.target.closest(".cm-item")
      if (!item) return
      const idx = parseInt(item.dataset.idx)
      if (idx !== this.selectedIdx) {
        this.selectedIdx = idx
        this._highlightSelected()
      }
    })

    document.body.appendChild(dd)
    this.dropdown = dd
  },

  _positionDropdown() {
    const rect = this.textarea.getBoundingClientRect()
    Object.assign(this.dropdown.style, {
      left: rect.left + "px",
      width: rect.width + "px",
      bottom: (window.innerHeight - rect.top + 4) + "px",
      top: "auto",
    })
  },

  // ── Mention detection (unified @ trigger) ─────────────────────────

  _detectMention() {
    const val = this.textarea.value
    const caret = this.textarea.selectionStart
    let scanPos = caret - 1
    let foundAt = -1

    while (scanPos >= 0) {
      const ch = val[scanPos]
      if (ch === "\n") break

      if (ch === "@") {
        const before = scanPos >= 1 ? val[scanPos - 1] : " "
        if (before === " " || before === "\n" || scanPos === 0) {
          foundAt = scanPos
        }
        break
      }
      scanPos--
    }

    if (foundAt === -1) {
      if (this.isOpen) this._close()
      return
    }

    const query = val.slice(foundAt + 1, caret)

    this.mentionStart = foundAt
    this._filter(query)

    if (this.candidates.length > 0) {
      if (!this.isOpen) this._open()
      else { this._renderItems(); this._positionDropdown() }
    } else {
      if (this.isOpen) this._close()
    }
  },

  _onKeydown(e) {
    if (!this.isOpen || this.candidates.length === 0) return

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        this.selectedIdx = Math.min(this.selectedIdx + 1, this.candidates.length - 1)
        this._highlightSelected()
        break
      case "ArrowUp":
        e.preventDefault()
        this.selectedIdx = Math.max(this.selectedIdx - 1, 0)
        this._highlightSelected()
        break
      case "Enter":
      case "Tab":
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        this._select(this.candidates[this.selectedIdx])
        break
      case "Escape":
        e.preventDefault()
        this._close()
        break
    }
  },

  // ── Filter: merge all types into unified list ─────────────────────

  _filter(query) {
    const q = query.toLowerCase()
    // One candidate with a null/empty name must never crash the filter —
    // same class of defect as the null-name sort crash documented in
    // _buildKnownMentions.
    const named = (list, key) =>
      (list || []).filter(x => x && typeof x[key] === "string" && x[key].length > 0)

    const fileResults = named(this.allFiles, "path")
      .filter(f => f.path.toLowerCase().includes(q))
      .map(f => ({ type: "file", label: f.path, value: f.path }))

    const appResults = named(this.allApps, "name")
      .filter(a => a.name.toLowerCase().includes(q))
      .map(a => ({ type: "app", label: a.name, value: a.name, id: a.id }))

    const agentResults = named(this.allAgents, "name")
      .filter(a => a.name.toLowerCase().includes(q))
      .map(a => ({ type: "agent", label: a.name, value: a.name, id: a.id }))

    const toolResults = named(this.allTools, "name")
      .filter(t => t.name.toLowerCase().includes(q) ||
                   (t.description || "").toLowerCase().includes(q))
      .map(t => {
        const src = t.source && t.source !== "platform" ? ` [${t.source}]` : ""
        return {
          type: "tool",
          label: t.name,
          sublabel: (t.description || "") + src,
          value: t.name,
          id: t.id
        }
      })

    const workflowResults = named(this.allWorkflows, "name")
      .filter(w => w.name.toLowerCase().includes(q))
      .map(w => ({ type: "workflow", label: w.name, sublabel: w.kind === "dag" ? "DAG" : "agent-only", value: w.name, id: w.id, kind: w.kind }))

    // Datapack mentions — pack-level (whole Data Model) listed FIRST so users
    // who type a partial pack name see "use entire pack" before individual
    // tables; table-level results follow for fine-grained selection.
    const packsByKey = {}
    ;(this.allDatapacks || []).forEach(d => {
      if (!d || !d.pack_key) return
      if (!packsByKey[d.pack_key]) {
        packsByKey[d.pack_key] = {
          pack_key: d.pack_key,
          engine: d.engine,
          tables: []
        }
      }
      packsByKey[d.pack_key].tables.push(d.table_name)
    })

    const packResults = Object.values(packsByKey)
      .filter(p => {
        const haystack = `data:${p.pack_key} ${p.engine || ""}`.toLowerCase()
        return haystack.includes(q)
      })
      .map(p => ({
        type: "datapack_pack",
        label: p.pack_key,
        sublabel:
          (p.engine === "ducklake" ? "Analytics" : "Data Model") +
          ` — entire pack (${p.tables.length} table${p.tables.length === 1 ? "" : "s"})`,
        value: `data:${p.pack_key}`,
        engine: p.engine
      }))

    const datapackResults = (this.allDatapacks || [])
      .filter(d => d && d.pack_key && d.table_name)
      .filter(d => {
        const haystack = `data:${d.pack_key}/${d.table_name} ${d.engine || ""}`.toLowerCase()
        return haystack.includes(q)
      })
      .map(d => ({
        type: "datapack",
        label: `${d.pack_key}.${d.table_name}`,
        sublabel: d.engine === "ducklake" ? "Analytics" : "Data Model",
        value: `data:${d.pack_key}/${d.table_name}`,
        engine: d.engine,
        has_grant: d.has_grant
      }))

    // Context blocks — only the ones the user toggled ON for this chat
    // are surfaced as mentions (see push_mention_candidates in
    // matthew_drawer_component.ex). `@context:<name>` is the inserted
    // mention; Matt picks it up via op_context_search (chat_thread scope).
    const contextResults = (this.allContextBlocks || [])
      .filter(b =>
        (b.name || "").toLowerCase().includes(q) ||
        (b.description || "").toLowerCase().includes(q)
      )
      .map(b => ({
        type: "context_block",
        label: b.name,
        sublabel: b.description ? b.description : "context block (active in chat)",
        value: `context:${b.name}`,
        id: b.id
      }))

    // Order: context > files > apps > agents > tools > workflows > datapacks.
    // Context blocks land first because they were explicitly enabled
    // for this chat — strong signal of user intent.
    this.candidates = [
      ...contextResults,
      ...fileResults,
      ...appResults,
      ...agentResults,
      ...toolResults,
      ...workflowResults,
      ...packResults,
      ...datapackResults
    ].slice(0, 10)
    this.selectedIdx = 0
  },

  // ── Open / Close ──────────────────────────────────────────────────

  _open() {
    this.isOpen = true
    this.textarea.dataset.mentionOpen = "true"
    this.dropdown.style.display = "block"
    this._positionDropdown()
    this._renderItems()
  },

  _close() {
    this.isOpen = false
    delete this.textarea.dataset.mentionOpen
    this.dropdown.style.display = "none"
    this.mentionStart = -1
    this.candidates = []
  },

  // ── Render (innerHTML only, listeners are delegated) ──────────────

  _renderItems() {
    const ICON_ATTRS = `fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"`
    const SPARKLES_SVG = `<svg width="12" height="12" viewBox="0 0 24 24" ${ICON_ATTRS}><path d="M12 3l1.7 5.1L19 10l-5.3 1.9L12 17l-1.7-5.1L5 10l5.3-1.9L12 3z"/><path d="M19 16v4"/><path d="M21 18h-4"/></svg>`
    const WRENCH_SVG = `<svg width="12" height="12" viewBox="0 0 24 24" ${ICON_ATTRS}><path d="M14.7 6.3a5 5 0 0 0 5.6 5.6l-7.9 7.9a3 3 0 0 1-4.2-4.2l7.9-7.9z"/><path d="M4 20l5.5-5.5"/></svg>`
    const BOLT_SVG = `<svg width="12" height="12" viewBox="0 0 24 24" ${ICON_ATTRS}><path d="M13 2 4 14h7l-1 8 10-13h-7l1-7z"/></svg>`
    const DATABASE_SVG = `<svg width="12" height="12" viewBox="0 0 24 24" ${ICON_ATTRS}><ellipse cx="12" cy="5" rx="7" ry="3"/><path d="M5 5v6c0 1.7 3.1 3 7 3s7-1.3 7-3V5"/><path d="M5 11v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6"/></svg>`
    const APP_SVG = `<svg width="12" height="12" viewBox="0 0 24 24" ${ICON_ATTRS}><path d="m12 3 8 4.5-8 4.5-8-4.5L12 3z"/><path d="m4 12 8 4.5 8-4.5"/><path d="m4 16.5 8 4.5 8-4.5"/></svg>`

    this.dropdown.innerHTML = this.candidates.map((c, i) => {
      const sel = i === this.selectedIdx ? " cm-sel" : ""
      const sublabel = c.sublabel
        ? `<span style="font-size:10px;color:var(--text-tertiary);margin-left:6px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">— ${this._escape(c.sublabel)}</span>`
        : ""

      if (c.type === "file") {
        const ext = c.value.split(".").pop()
        return `<div class="cm-item${sel}" data-idx="${i}" style="padding:6px 10px;cursor:pointer;display:flex;align-items:center;gap:8px;border-radius:4px;margin:2px 4px;">
          <span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:4px;background:var(--accent-primary);color:white;font-size:9px;font-weight:600;flex-shrink:0">${ext}</span>
          <span style="font-size:12px;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${this._escape(c.label)}</span>
        </div>`
      } else if (c.type === "agent") {
        return `<div class="cm-item${sel}" data-idx="${i}" style="padding:6px 10px;cursor:pointer;display:flex;align-items:center;gap:8px;border-radius:4px;margin:2px 4px;">
          <span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:4px;background:var(--warn);color:white;font-size:9px;flex-shrink:0">
            ${SPARKLES_SVG}
          </span>
          <span style="font-size:12px;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${this._escape(c.label)}</span>
        </div>`
      } else if (c.type === "tool") {
        // Wrench icon — heroicons "wrench-screwdriver" minified
        return `<div class="cm-item${sel}" data-idx="${i}" style="padding:6px 10px;cursor:pointer;display:flex;align-items:center;gap:8px;border-radius:4px;margin:2px 4px;">
          <span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:4px;background:var(--info);color:white;font-size:9px;flex-shrink:0">
            ${WRENCH_SVG}
          </span>
          <span style="font-size:12px;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;min-width:0">${this._escape(c.label)}${sublabel}</span>
        </div>`
      } else if (c.type === "workflow") {
        // Bolt icon for workflow triggers
        return `<div class="cm-item${sel}" data-idx="${i}" style="padding:6px 10px;cursor:pointer;display:flex;align-items:center;gap:8px;border-radius:4px;margin:2px 4px;">
          <span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:4px;background:var(--badge-workflow);color:white;font-size:9px;flex-shrink:0">
            ${BOLT_SVG}
          </span>
          <span style="font-size:12px;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;min-width:0">${this._escape(c.label)}${sublabel}</span>
        </div>`
      } else if (c.type === "datapack" || c.type === "datapack_pack") {
        // Database/cylinder icon — Seiva green for Data Models, neutral for Analytics.
        // datapack = single table; datapack_pack = entire pack (broader handle).
        const bg = c.engine === "ducklake" ? "var(--n-600)" : "var(--s-300)"
        const grantHint = c.has_grant ? " ✓" : ""
        const labelMarkup =
          c.type === "datapack_pack"
            ? `<strong style="font-weight:600">${this._escape(c.label)}</strong>`
            : this._escape(c.label) + grantHint
        return `<div class="cm-item${sel}" data-idx="${i}" style="padding:6px 10px;cursor:pointer;display:flex;align-items:center;gap:8px;border-radius:4px;margin:2px 4px;">
          <span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:4px;background:${bg};color:white;font-size:9px;flex-shrink:0">
            ${DATABASE_SVG}
          </span>
          <span style="font-size:12px;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;min-width:0">${labelMarkup}${sublabel}</span>
        </div>`
      } else {
        // app
        return `<div class="cm-item${sel}" data-idx="${i}" style="padding:6px 10px;cursor:pointer;display:flex;align-items:center;gap:8px;border-radius:4px;margin:2px 4px;">
          <span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:4px;background:var(--info);color:white;font-size:9px;flex-shrink:0">
            ${APP_SVG}
          </span>
          <span style="font-size:12px;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${this._escape(c.label)}</span>
        </div>`
      }
    }).join("")
  },

  _escape(s) {
    return String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  },

  // Highlight without re-creating DOM (fast, preserves delegated listeners)
  _highlightSelected() {
    this.dropdown.querySelectorAll(".cm-item").forEach((el, i) => {
      el.classList.toggle("cm-sel", i === this.selectedIdx)
    })
  },

  // ── Select: always insert @name ───────────────────────────────────

  _select(candidate) {
    if (!candidate) {
      // A stale/empty selection must not leave the dropdown (and the
      // `mentionOpen` Enter guard) stuck — close so Enter submits again.
      this._close()
      return
    }
    const val = this.textarea.value
    const before = val.slice(0, this.mentionStart)
    const afterCaret = val.slice(this.textarea.selectionStart)
    const insertion = `@${candidate.value} `
    const newVal = before + insertion + afterCaret
    const newCaret = before.length + insertion.length

    this._close()

    // Set value via native setter to ensure LiveView sees the change
    const nativeSetter = Object.getOwnPropertyDescriptor(
      HTMLTextAreaElement.prototype, "value"
    ).set
    nativeSetter.call(this.textarea, newVal)

    this.textarea.selectionStart = newCaret
    this.textarea.selectionEnd = newCaret
    this.textarea.focus()
    this._resize()

    // Notify LiveView
    this.textarea.dispatchEvent(new Event("input", { bubbles: true }))
  },
}
